import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ApiErrorComponent, extractHttpErrorMessage } from '@comparit/core';
import { PageNavigatorService, TaskContextService } from '@comparit/shared';
import { take } from 'rxjs';
import type { ErgebnisInput } from '../../interfaces/ergebnis-input.model';
import { TarifdetailsBaseService } from '../../services/tarifdetails-base.service';

/**
 * `/tarifdetails-laden` — the bridge between a chosen tariff and its Tarifdetails page: it looks
 * the tariff up in the current run, creates the Vorgang, and replaces itself with
 * `/tarifdetails/:vorgangId` so Back skips it.
 */
@Component({
  selector: 'ui-tarifdetails-laden',
  templateUrl: './tarifdetails-laden.component.html',
  styleUrl: './tarifdetails-laden.component.scss',
  imports: [MatProgressSpinnerModule, ApiErrorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TarifdetailsLadenComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly taskContext = inject(TaskContextService);
  private readonly navigator = inject(PageNavigatorService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tarifdetails = inject(TarifdetailsBaseService);

  protected readonly error = signal<string | null>(null);

  ngOnInit(): void {
    this.navigator.setup.set(null);
    // Whatever happens next, the customer got here from a page worth going back to.
    this.tarifdetails.openedInApp.set(true);

    const params = this.route.snapshot.queryParamMap;
    const berechnungId = params.get('berechnungId');
    const kombinationId = params.get('kombinationId');

    if (!berechnungId || !kombinationId) {
      void this.router.navigateByUrl(this.tarifdetails.backUrl);
      return;
    }

    this.tarifdetails
      .findTarif$({ berechnungId, kombinationId })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(tarif => {
        if (!tarif) {
          this.error.set('Tarifdaten nicht gefunden.');
          return;
        }
        this.createVorgang(tarif);
      });
  }

  protected goBack(): void {
    this.tarifdetails.goBack();
  }

  private createVorgang(tarif: ErgebnisInput): void {
    this.tarifdetails
      .createVorgang$(tarif)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ vorgangId }) => {
          const taskId = this.taskContext.taskId();
          if (taskId) this.taskContext.initFromRestore(taskId, vorgangId);

          void this.router.navigateByUrl(this.tarifdetails.detailsUrl(vorgangId), {
            replaceUrl: true,
          });
        },
        error: (err: unknown) => this.error.set(extractHttpErrorMessage(err)),
      });
  }
}
